import React, { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * Mobile-friendly camera barcode scanner. Prefers the native BarcodeDetector
 * (fast, on-device) and falls back to @zxing/browser for Code 128 labels.
 * Processes frames locally, releases the camera on close/unmount, and reports
 * each successful scan once (repeated frames never double-fire).
 */
export default function BarcodeScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const controlsRef = useRef(null);
  const scannedRef = useRef(false);
  const [error, setError] = useState("");
  const [devices, setDevices] = useState([]);
  const [activeDevice, setActiveDevice] = useState("");
  const [usingNative, setUsingNative] = useState(false);

  const stopStream = useCallback(() => {
    try {
      controlsRef.current?.stop?.();
    } catch { /* noop */ }
    controlsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const handleResult = useCallback((text) => {
    if (scannedRef.current) return;
    const value = String(text || "").trim();
    if (!value) return;
    scannedRef.current = true;
    stopStream();
    onScan(value);
  }, [onScan, stopStream]);

  const startWithNative = useCallback(async (deviceId) => {
    const detector = new window.BarcodeDetector({ formats: ["code_128", "code_39", "ean_13", "ean_8", "qr_code", "upc_a", "upc_e"] });
    const stream = await navigator.mediaDevices.getUserMedia({
      video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: "environment" },
      audio: false,
    });
    streamRef.current = stream;
    const video = videoRef.current;
    video.srcObject = stream;
    await video.play();
    const tick = async () => {
      if (!streamRef.current || scannedRef.current) return;
      try {
        const codes = await detector.detect(video);
        if (codes && codes.length) {
          handleResult(codes[0].rawValue);
          return;
        }
      } catch { /* keep scanning */ }
      requestAnimationFrame(tick);
    };
    tick();
  }, [handleResult]);

  const startWithZxing = useCallback(async (deviceId) => {
    const { BrowserMultiFormatReader } = await import("@zxing/browser");
    const reader = new BrowserMultiFormatReader();
    const controls = await reader.decodeFromVideoDevice(
      deviceId || undefined,
      videoRef.current,
      (result, err, controls) => {
        controlsRef.current = controls;
        if (result) handleResult(result.getText());
      },
    );
    controlsRef.current = controls;
    streamRef.current = videoRef.current?.srcObject || null;
  }, [handleResult]);

  const start = useCallback(async (deviceId) => {
    setError("");
    scannedRef.current = false;
    stopStream();
    try {
      if (window.BarcodeDetector) {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        if (supported.includes("code_128")) {
          setUsingNative(true);
          await startWithNative(deviceId);
          return;
        }
      }
      setUsingNative(false);
      await startWithZxing(deviceId);
      const stream = videoRef.current?.srcObject;
      if (stream) {
        const devicesList = await navigator.mediaDevices.enumerateDevices();
        setDevices(devicesList.filter((device) => device.kind === "videoinput"));
      }
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError("Camera permission denied. Allow camera access or type the code below.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No usable camera found. Type the code below instead.");
      } else {
        setError("Could not start the camera. Check the connection or type the code below.");
      }
    }
  }, [startWithNative, startWithZxing, stopStream]);

  useEffect(() => {
    start(activeDevice || undefined);
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDevice]);

  const scanAgain = () => {
    scannedRef.current = false;
    start(activeDevice || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" aria-label="Barcode scanner">
      <div className="w-full max-w-md rounded-2xl border border-stone-100/10 bg-zinc-950 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg text-stone-100">Scan barcode</h3>
          <button type="button" onClick={onClose} aria-label="Close scanner" className="rounded-md p-2 text-stone-400 hover:bg-stone-100/10">
            <X className="h-5 w-5" />
          </button>
        </div>
        {error ? (
          <p className="rounded-md border border-rose-400/30 bg-rose-950/20 p-3 text-sm text-rose-200">{error}</p>
        ) : (
          <div className="relative overflow-hidden rounded-lg bg-black">
            <video ref={videoRef} className="aspect-[4/3] w-full object-cover" muted playsInline />
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="h-24 w-4/5 rounded-md border-2 border-[#26C6D5]/80" />
            </div>
          </div>
        )}
        {devices.length > 1 && (
          <select
            value={activeDevice}
            onChange={(event) => setActiveDevice(event.target.value)}
            className="mt-3 w-full rounded-md border border-stone-100/10 bg-zinc-900 px-3 py-2 text-sm text-stone-200"
            aria-label="Select camera"
          >
            {devices.map((device, index) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Camera ${index + 1}`}
              </option>
            ))}
          </select>
        )}
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={scanAgain} className="flex-1 rounded-md border border-stone-100/15 px-4 py-2 text-sm text-stone-200 hover:bg-stone-100/10">
            Scan again
          </button>
          <button type="button" onClick={onClose} className="flex-1 rounded-md bg-[#D8A34B] px-4 py-2 text-sm font-medium text-[#15191C] hover:bg-[#c8963f]">
            Close
          </button>
        </div>
        <p className="mt-2 text-center text-xs text-stone-500">{usingNative ? "Native scanner" : "ZXing scanner"} · frames processed on this device</p>
      </div>
    </div>
  );
}
